<template>
  <div class="hydrogen-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$E_n = -13.6 / n² eV$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan"><label>当前能级 n</label><span class="val">{{ currentLevel }}</span></div>
          <div class="hud-item orange"><label>最近释放光子 (λ)</label><span class="val" v-if="lastLambda">{{ lastLambda.toFixed(1) }} <small>nm</small></span><span v-else class="val">--</span></div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <!-- 轨道模型 (Orbit Tracking) -->
          <g transform="translate(140, 120)">
             <circle v-for="n in 5" :key="'o'+n" :r="n*18" fill="none" stroke="#1e293b" stroke-width="1" />
             <circle r="6" fill="#ef4444" shadow="0 0 10px #f87171" /> <!-- 质子 -->
             
             <!-- 电子动画 -->
             <circle :r="4" fill="#06b6d4" :cx="electronX" :cy="0" filter="url(#glow-e)">
                <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" :dur="`${0.5 * currentLevel}s`" repeatCount="indefinite" />
             </circle>
          </g>

          <!-- 离散能级图 (Energy Levels) -->
          <g transform="translate(320, 30)">
             <text x="0" y="-10" fill="#475569" font-size="9" font-weight="900">能级图表 E(n)</text>
             <g v-for="n in 5" :key="'l'+n" class="e-line" @click="jump(n)">
                <line x1="0" :y1="getE_Y(n)" x2="100" :y2="getE_Y(n)" 
                      :stroke="currentLevel === n ? '#06b6d4' : '#334155'" :stroke-width="currentLevel === n ? 3 : 1.5" />
                <text x="-15" :y="getE_Y(n)+3" :fill="currentLevel === n ? '#06b6d4' : '#475569'" font-size="10">n={{n}}</text>
             </g>
          </g>

          <!-- 飞出的光子 (Photon Pulse) -->
          <g v-if="photonActive" :transform="`translate(${photonX}, ${photonY})`">
             <path d="M 0 0 Q 5 -8, 10 0 T 20 0 T 30 0" fill="none" :stroke="lastColor" stroke-width="2">
                <animate attributeName="opacity" values="1;0" dur="0.8s" repeatCount="1" />
             </path>
          </g>

          <defs>
             <filter id="glow-e"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>
        </svg>

        <!-- 这里的指示灯解决了“调参看不出关系”的问题 -->
        <div class="spectrum-viewer">
           <div class="bar-desc">光谱分析仪 (λ/nm)</div>
           <div class="rainbow-bar">
              <div v-if="lastLambda" class="peak-line" :style="{ left: lambdaX + '%', background: lastColor, boxShadow: `0 0 10px ${lastColor}` }"></div>
           </div>
           <div class="labels"><span>紫外</span><span>400</span><span>500</span><span>600</span><span>700</span><span>红外</span></div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">电子能级操作 (直接点击右侧能级线或滑块)</label>
                 <el-slider v-model="currentLevel" :min="1" :max="5" :step="1" @change="jump(currentLevel)"></el-slider>
              </div>
              <div class="msg-box">
                 <span v-if="jumpLog" class="fade-tag">{{ jumpLog }}</span>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-medal"></i>
           <p><strong>实战物理：</strong>原子的能量是不连续的。当电子从高能级 $ n_2 $ “跳”到低能级 $ n_1 $ 时，多余的能量以单光子形式射出。<strong>观察：</strong>点击 $ n=3, 4, 5 \to 2 $，光谱仪上会出现红、青、蓝色的**巴耳末线**。这是氢原子独一无二的“指纹”，正是通过这些线，人类才破解了宇宙中恒星的成分。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'HydrogenSpectrumViz',
  data() {
    return {
      currentLevel: 1,
      lastLambda: null,
      lastColor: '#fff',
      lambdaX: 0,
      photonActive: false,
      photonX: 180,
      photonY: 120,
      jumpLog: '',
      electronX: 18
    }
  },
  methods: {
    getE_Y(n) {
       // Hyperbolic mapping: E = -13.6/n^2. Total 160px spread.
       return 160 * (1 - 1/(n*n)); 
    },
    jump(n) {
       if (n === this.currentLevel) return;
       const from = this.currentLevel;
       const to = n;
       this.currentLevel = n;
       this.electronX = n * 18;
       
       if (from > to) {
          // Emission
          const de = 13.6 * (1/(to*to) - 1/(from*from));
          const wave = 1240 / de;
          this.lastLambda = wave;
          this.updateSpectrum(wave, to, from);
          this.triggerPhoton();
          this.jumpLog = `跃迁: n=${from}→${to} 发射光子 (ΔE=${de.toFixed(2)}eV)`;
       } else {
          this.jumpLog = `跃迁: n=${from}→${to} 吸收能量`;
          this.lastLambda = null;
       }
    },
    updateSpectrum(wave, to, from) {
       // Mapping visible: 400nm (left 10%) to 750nm (right 90%)
       this.lambdaX = ((wave - 300) / 600) * 100;
       if (to === 2) { // Balmer
          if (from === 3) this.lastColor = '#ef4444';      // H-alpha Red
          else if (from === 4) this.lastColor = '#06b6d4'; // H-beta Cyan
          else if (from === 5) this.lastColor = '#3b82f6'; // H-gamma Blue
          else this.lastColor = '#8b5cf6';
       } else if (to === 1) { // Lyman (UV)
          this.lastColor = '#a855f7';
       } else { // Paschen (IR)
          this.lastColor = '#f43f5e';
       }
    },
    triggerPhoton() {
       this.photonActive = true;
       this.photonX = 140 + this.currentLevel * 18;
       this.photonY = 120;
       setTimeout(() => {
          let start = Date.now();
          const move = () => {
             const dt = Date.now() - start;
             if (dt < 600) {
                this.photonX += 5;
                this.photonY = 120 + Math.sin(dt * 0.05) * 6;
                requestAnimationFrame(move);
             } else {
                this.photonActive = false;
             }
          }
          move();
       }, 50);
    }
  },
  beforeDestroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.timer) clearInterval(this.timer);
  },
}
</script>

<style scoped lang="scss">
.hydrogen-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #dcfce7; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f97316; } } }
.spectrum-viewer { position: absolute; bottom: 12px; width: 90%; left: 5%; background: rgba(15,23,42,0.6); padding: 8px; border-radius: 8px; .bar-desc { font-size: 9px; color: #64748b; margin-bottom: 4px; font-weight: 900; } .rainbow-bar { height: 12px; background: linear-gradient(to right, #000 0%, #a855f7 15%, #3b82f6 30%, #22c55e 50%, #facc15 70%, #ef4444 85%, #000 100%); border-radius: 2px; position: relative; .peak-line { position: absolute; top: -4px; width: 2px; height: 20px; z-index: 10; } } .labels { display: flex; justify-content: space-between; font-size: 8px; color: #475569; margin-top: 2px; } }
.e-line { cursor: pointer; &:hover line { stroke: #94a3b8; } }
.msg-box { height: 32px; flex: 1; display: flex; align-items: center; justify-content: center; }
.fade-tag { background: #1e293b; color: #f1f5f9; padding: 4px 12px; border-radius: 12px; font-size: 11px; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 4px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .flex-row { flex-direction: row; } .flex-1 { flex: 1; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #dcfce7; display: flex; align-items: center; gap: 10px; i { color: #10b981; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #166534; line-height: 1.5; strong { color: #14532d; } } }
</style>
